<template>
  <div class="report-community-space-container">
    <el-row :gutter="16">
      <!-- 左侧场馆导航 + 月份选择 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-nav-card">
          <div class="ds-nav-card__header">{{ $t('reportCommunitySpace.venueList') }}</div>
          <div class="ds-nav-card__filter">
            <el-date-picker v-model="queryParams.appointmentTime" type="month" class="w-full"
              :placeholder="$t('reportCommunitySpace.selectMonth')" value-format="yyyy-MM" @change="fetchData" />
          </div>
          <el-scrollbar class="ds-nav-card__scroll">
            <ul class="ds-nav-card__list">
              <li v-for="(item, index) in venues" :key="index" class="ds-nav-card__item"
                :class="{ 'is-active': queryParams.venueId === item.venueId }" @click="selectVenue(item)">
                <i class="el-icon-office-building"></i>
                <span class="ds-nav-card__name">{{ item.name }}</span>
              </li>
            </ul>
          </el-scrollbar>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('reportCommunitySpace.page.title')" :subtitle="subtitleText">
          <template #extra>
            <el-button icon="el-icon-refresh" @click="fetchData">
              {{ $t('common.refresh') }}
            </el-button>
          </template>
        </ds-page-header>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="communitySpaces.length" :show-column-setting="false" :show-density="false"
            :show-refresh="true" @refresh="fetchData">
            <template #left>
              <span class="ds-panel__title">{{ $t('reportCommunitySpace.scheduleTitle') }}</span>
              <el-tooltip :content="$t('reportCommunitySpace.page.tip')" placement="top">
                <i class="el-icon-info ds-panel__info" />
              </el-tooltip>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            border :empty-description="$t('reportCommunitySpace.page.empty')" @retry="fetchData">
            <el-table-column prop="time" :label="$t('reportCommunitySpace.appointmentTime')" width="100"
              align="center" fixed="left">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.time }}:00</span>
              </template>
            </el-table-column>
            <template v-if="communitySpaces.length > 0">
              <el-table-column v-for="space in communitySpaces" :key="space.spaceId" :label="space.name" align="center"
                min-width="140">
                <template slot-scope="{ $index }">
                  <!-- 可预约：文字按钮，保持原有「点击新增预约」语义 -->
                  <div v-if="getSpaceStatus($index, space) === 'available'">
                    <el-button type="text" class="ds-btn-link" @click="openAddModal(space.spaceId, $index)">
                      {{ $t('reportCommunitySpace.available') }}
                    </el-button>
                  </div>
                  <!-- 不可预约 / 已占用 -->
                  <div v-else-if="getSpaceStatus($index, space) === $t('reportCommunitySpace.notAvailable')">
                    <ds-status-tag type="muted" :label="$t('reportCommunitySpace.notAvailable')" />
                  </div>
                  <div v-else>
                    <ds-status-tag type="primary" :label="getSpaceStatus($index, space)" />
                  </div>
                </template>
              </el-table-column>
            </template>
            <el-table-column v-else :label="$t('reportCommunitySpace.noSpace')" align="center" />
          </ds-data-table>

          <ds-pagination-bar :total="communitySpaces.length" :page="1" :size="communitySpaces.length || 10"
            :page-sizes="[10, 20, 30, 50]">
            <template #summary>
              <span class="ds-tip-text">{{ $t('reportCommunitySpace.page.summary') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>

    <add-community-space-person ref="addPersonDialog" @success="fetchData" />
  </div>
</template>

<script>
import { listCommunityVenue, listCommunitySpace, listCommunitySpacePerson } from '@/api/community/reportCommunitySpaceApi'
import AddCommunitySpacePerson from '@/components/community/addCommunitySpacePerson'
import { getCommunityId } from '@/api/community/communityApi'
import { getDateYYYYMMDD } from '@/utils/dateUtil'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ReportCommunitySpaceList',
  components: {
    AddCommunitySpacePerson,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      queryParams: {
        venueId: '',
        appointmentTime: getDateYYYYMMDD(),
        communityId: getCommunityId()
      },
      venues: [],
      communitySpaces: [],
      spacePersons: [],
      tableData: Array.from({ length: 24 }, (_, i) => ({ time: `${i}` }))
    }
  },
  computed: {
    /**
     * 副标题：当前场馆 + 预约月份
     */
    subtitleText() {
      const venue = this.venues.find(v => v.venueId === this.queryParams.venueId)
      const venueName = venue ? venue.name : ''
      const month = this.queryParams.appointmentTime || ''
      const parts = []
      if (venueName) parts.push(venueName)
      if (month) parts.push(month)
      return parts.length
        ? parts.join(' · ') + ' · ' + this.$t('reportCommunitySpace.page.subtitle')
        : this.$t('reportCommunitySpace.page.subtitle')
    }
  },
  created() {
    this.fetchVenues()
  },
  methods: {
    selectVenue(venue) {
      this.queryParams.venueId = venue.venueId
      this.fetchData()
    },
    async fetchVenues() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.queryParams.communityId
        }
        this.venues = await listCommunityVenue(params)
        if (this.venues.length > 0) {
          this.queryParams.venueId = this.venues[0].venueId
          this.fetchData()
        }
      } catch (error) {
        this.$message.error(this.$t('common.fetchFailed'))
      }
    },
    async fetchData() {
      if (!this.queryParams.venueId) return

      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        // 获取场地列表
        const spaceParams = {
          page: 1,
          row: 100,
          venueId: this.queryParams.venueId,
          communityId: this.queryParams.communityId
        }
        this.communitySpaces = await listCommunitySpace(spaceParams)

        // 获取预约记录
        const personParams = {
          page: 1,
          row: 100,
          venueId: this.queryParams.venueId,
          appointmentTime: this.queryParams.appointmentTime,
          communityId: this.queryParams.communityId,
          state: 'S'
        }
        this.spacePersons = await listCommunitySpacePerson(personParams)
      } catch (error) {
        console.error('获取场地排班失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('reportCommunitySpace.page.loadFailed')
        this.$message.error(this.$t('common.fetchFailed'))
      } finally {
        this.loading = false
      }
    },
    getSpaceStatus(hour, space) {
      // 检查场地是否在该小时不可用
      const openTime = space.openTimes.find(t => t.hours == hour)
      if (openTime && openTime.isOpen === 'N') {
        return this.$t('reportCommunitySpace.notAvailable')
      }

      // 检查是否有预约
      const person = this.spacePersons.find(p => p.spaceId === space.spaceId)
      if (person) {
        const timeSlot = person.times.find(t => t.hours == hour)
        if (timeSlot) {
          return `${person.personName} > ${person.personTel}`
        }
      }

      return 'available'
    },
    openAddModal(spaceId, hour) {
      const data = {
        spaceId: spaceId,
        hours: hour,
        appointmentTime: this.queryParams.appointmentTime,
        openTime: hour
      }
      this.$refs.addPersonDialog.open(data)
    }
  }
}
</script>

<style lang="scss" scoped>
.report-community-space-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧场馆导航卡片 */
.ds-nav-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__filter {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__scroll {
    max-height: calc(100vh - 320px);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-xs);
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    cursor: pointer;
    transition: background-color var(--ds-transition-fast),
      color var(--ds-transition-fast);

    &:hover {
      background-color: rgba(var(--ds-primary-rgb), 0.08);
      color: var(--ds-primary);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__info {
    color: var(--ds-gray-400);
    cursor: pointer;
    transition: color var(--ds-transition-fast);

    &:hover {
      color: var(--ds-primary);
    }
  }
}

/* 可预约入口（文字按钮） */
.ds-btn-link {
  font-weight: 500;
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
